import React, { useEffect, useRef, useState } from 'react';
import { Alert, Button, Col, Heading, Link, Row, Spinner, themes } from '@digitools/honest-react';
import { Breadcrumb, Breadcrumbs } from '@digitools/honest-ui';
import styled from 'styled-components';
import useDentalDataContext from './hooks/useDentalContext';
import MemberInformationBox from 'src/packages/dental/components/MemberInformationBox';
import TabContent from './components/TabContent';
import useMediaQuery from 'src/hooks/useMediaQuery';
import MemberDropdown from 'src/packages/dental/components/MemberDropdown';
import DentalText from 'src/packages/dental/constants/DentalText';
import dentalText from 'src/packages/dental/constants/DentalText';
import useTranslate from 'src/hooks/useTranslate';
import { CLAIMS, COVERED_SERVICES, DEDUCTIBLES_AND_COINSURANCE, getMemberEvent, tealiumFormat, } from 'src/packages/dental/providers/DentalDataProvider';
import { GetIdCardPdf, GetPdf } from 'src/packages/dental/api/DentalApiCalls';
import useAuthentication from 'src/hooks/useAuthentication';
import { DentalTile, dentalTileTypeEnum } from 'src/packages/dental/components/DentalTile';
import { TEALIUM_EVENT } from 'src/constants/tealiumConstants';
import AdditionalResources from './components/AdditionalResources';
import { Language } from 'src/types/Language';
import { useHomePageBreadcrumbs } from 'src/components/Breadcrumbs';
import { getPdfFilename, saveFile } from 'src/utils/pdfUtil';
const StyledNavSelectIndicator = () => {
    return (<div style={{
            content: '',
            height: '50%',
            width: '3px',
            position: 'absolute',
            left: '0',
            top: '50%',
            transform: 'translateY(-50%)',
            background: `${themes.publicSite.palette.primary}`,
        }}></div>);
};
export const StyledSideMenu = styled(Button) `
  color: ${({ theme }) => `${theme.palette.black}`};
  font-weight: 400;
  border-top: ${({ theme }) => `1px solid ${theme.palette.grayLighter}`};
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
  position: relative;
  text-align: left;
  margin-left: 0;
  margin-right: 0;
  padding: 1rem 1rem 2.25rem 1rem;

  &:last-child {
    border-bottom: ${({ theme }) => `1px solid ${theme.palette.grayLighter}`};
  }
`;
export const DentalTabPanel = ({ children }) => {
    const { activeTab } = useDentalDataContext();
    return <div role='tabpanel' tabIndex={0} id={activeTab.replace(' ', '_') + '-tab-panel'} aria-labelledby={activeTab.replace(' ', '_') + '-tab-button'}>{children}</div>;
};
export const DentalHeading = ({ text, isTitleView }) => {
    return (<Heading elemType={isTitleView ? 'h3' : 'h1'} elemStyle={isTitleView ? 'h2' : 'h1'} style={{ fontSize: isTitleView ? '28px' : '42px' }} data-public={true}>
      {text}
    </Heading>);
};
export const DentalSubHeading = ({ text, isTitleView, color = 'black', className }) => {
    const paletteColor = color;
    return (<Heading elemType={isTitleView ? 'h4' : 'h2'} className={className ? className : ''} color={paletteColor} elemStyle={isTitleView ? 'h2' : 'h1'} style={{ fontSize: isTitleView ? '21px' : '33px' }}>
      {text}
    </Heading>);
};
export const ErrorHtml = ({ errorType, generalErrorMessage, notFoundErrorMessage }) => {
    const { error } = useDentalDataContext();
    errorType = errorType || error;
    const { t } = useTranslate();
    return errorType && errorType.indexOf('NOT_FOUND') > -1 ? (<Alert type={'warning'}>{notFoundErrorMessage || t(DentalText.deductibleAndCoinsuranceNotFoundWarning)}</Alert>) : (<Alert type={'error'}>{generalErrorMessage || t(DentalText.generalError)}</Alert>);
};
export const TabsDisclaimerText = () => {
    const isTitleView = useMediaQuery('(max-width: 780px)');
    const { subscriber, activeTab } = useDentalDataContext();
    const isClaimsInNetworkOnly = (subscriber?.planOverview?.inNetworkOnlyClause && activeTab == CLAIMS);
    const { t } = useTranslate();
    return <Row className={'pb-0 mb-0'}>
    <Col className={isTitleView && !isClaimsInNetworkOnly && activeTab != CLAIMS ? 'mt-0 pt-0 mb-0' : isTitleView ? 'mt-3' : 'mt-2'}>
      <section aria-label={'tabDisclaimer'} className={isTitleView ? 'mb-3' : 'mb-0'}>
        <p data-public={true}>{t(DentalText.disclaimerText1) + subscriber?.asOfDate + t(DentalText.disclaimerText2)}</p>
      </section>
    </Col>
  </Row>;
};
export const DisclaimerText = ({ action, idCardLoading }) => {
    const { subscriber } = useDentalDataContext();
    const { t } = useTranslate();
    return (<section aria-label={'dentalDisclaimer'} data-testid={'disclaimer'} style={{ color: 'gray', fontSize: '.5rem' }} data-public={true}>
      <p className={'small'}>
        <p>
          {subscriber?.planOverview?.planFundingClause}
          {t(dentalText.disclaimerTextPlanFundingAddition)}
        </p>
        <p>{t(DentalText.hipaaDisclaimer)}</p>
        <p>{t(DentalText.obtainDisclaimerText)}</p>
        <ul style={{ marginTop: 0 }}>
          <li style={{ listStylePosition: 'outside', marginLeft: '2rem' }}>
            <span>{t(DentalText.authorizationFormDisclaimer)}</span>
          </li>
          <li style={{
            listStylePosition: 'outside',
            marginLeft: '2rem',
            marginTop: 0,
        }}>
            {t(DentalText.dentalInfoReleaseDisclaimerA)}
            <Link style={{ fontSize: '.8rem' }} target={'_blank'} href={'https://www.lincoln4benefits.com/adminWebContent/Production/Ardis_Forms/grup/BJ-03566.pdf?_cmatype=open'}>
              {t(DentalText.dentalInfoReleaseFormLinkText)}
            </Link>
            {t(DentalText.dentalInfoReleaseDisclaimerB)}
          </li>
        </ul>
        <p>
          {t(DentalText.continuedParticipationDisclaimerA)}
          <Button id={'disclaimer-text-id-card-button'} className={'m-0 p-0'} style={{ fontSize: '.8rem' }} onClick={() => action()} disabled={idCardLoading}>
            {t(DentalText.dentalIdCardLink)}
          </Button>
          {t(DentalText.continuedParticipationDisclaimerB)}
        </p>
      </p>
    </section>);
};
const Dental = () => {
    const { subscriber, loading, activeTab, tealiumFunction, setActiveTab, currentMember, error, claimsLoading, deductiblesLoading, coveredServicesLoading, setCurrentMember, setBenefitsPdfLoading, benefitsPdfLoading, hasLanguageWarning, setHasLanguageWarning, idCardLoading, setIdCardLoading, } = useDentalDataContext();
    const { t } = useTranslate();
    const [benefitsPdfError, setBenefitsPdfError] = useState('');
    const [idCardPdfError, setIdCardPdfError] = useState('');
    const isMobile = useMediaQuery('(max-width: 1439px)');
    const isTitleView = useMediaQuery('(max-width: 780px)');
    const { authHeader } = useAuthentication();
    const memberEvent = getMemberEvent(currentMember, subscriber);
    const downloadBenefitsButtonRef = useRef(null);
    useEffect(() => {
        setIdCardPdfError('');
        setBenefitsPdfError('');
    }, [activeTab, currentMember]);
    const IdCardAction = () => {
        if (!idCardLoading && currentMember?.status != 'INACTIVE') {
            setIdCardLoading(true);
            setIdCardPdfError('');
            setBenefitsPdfError('');
            processIdCard();
            document.getElementById(t(DentalText.IdCardTitle).replace(/[^a-zA-Z0-9]/g, '-') + '-button')?.focus();
        }
    };
    if (loading) {
        return <Spinner data-testid='dental-spinner'/>;
    }
    const processBenefitsPdf = () => {
        //remove hardcoded language when we support Spanish
        GetPdf(Language.ENGLISH, authHeader(), currentMember?.memberType, currentMember?.id)
            .then((response) => {
            const fileName = getPdfFilename('', currentMember?.firstName || '', currentMember?.lastName || '', new Date());
            saveFile(response, fileName);
            tealiumFunction({
                memberEvent: memberEvent,
                eventName: tealiumFormat(downloadBenefitsTitle),
                eventAction: TEALIUM_EVENT.EVENT_ACTION.DOWNLOAD,
            });
        })
            .catch(error => {
            setBenefitsPdfError(t(DentalText.benefitsPdfError));
            tealiumFunction({
                memberEvent: memberEvent,
                eventName: tealiumFormat(t(DentalText.benefitsPdfError)),
                eventAction: TEALIUM_EVENT.EVENT_ACTION.ERROR,
            });
        })
            .finally(() => {
            setBenefitsPdfLoading(false);
            // Restore focus to the download button after browser download dialog
            setTimeout(() => {
                downloadBenefitsButtonRef.current?.focus();
            }, 0);
        });
    };
    const processIdCard = () => {
        //remove hardcoded language when we support Spanish
        GetIdCardPdf(Language.ENGLISH, authHeader())
            .then((response) => {
            const fileName = getPdfFilename('id-card', currentMember?.firstName || '', currentMember?.lastName || '', new Date());
            saveFile(response, fileName);
            tealiumFunction({
                memberEvent: memberEvent,
                eventName: tealiumFormat(t(DentalText.IdCardTitle)),
                eventAction: TEALIUM_EVENT.EVENT_ACTION.DOWNLOAD,
            });
        })
            .catch(error => {
            setIdCardPdfError(t(DentalText.idCardPdfError));
            tealiumFunction({
                memberEvent: memberEvent,
                eventName: tealiumFormat(t(DentalText.idCardPdfError)),
                eventAction: TEALIUM_EVENT.EVENT_ACTION.ERROR,
            });
        })
            .finally(() => {
            setIdCardLoading(false);
        });
    };
    const idCardTitle = t(DentalText.IdCardTitle);
    const findADentistTitle = t(DentalText.findADentistTitle);
    const downloadBenefitsTitle = isTitleView
        ? t(DentalText.downloadBenefitsTitleSmallest)
        : t(DentalText.downloadBenefitsTitle);
    const MemberInformationButtons = ({ isTitleView, isMobileView, isLeftCentered, }) => {
        return (<>
        <DentalTile type={dentalTileTypeEnum.BUTTON} isInline={!isMobileView} ariaLabel={currentMember?.status === 'INACTIVE' ? 'ID card disabled because member is inactive' : undefined} disabled={idCardLoading || currentMember?.status == 'INACTIVE'} text={isTitleView ? undefined : t(DentalText.IdCardText)} action={IdCardAction} title={idCardTitle} imageName='id-card' isLeftCentered={isLeftCentered}/>
        <DentalTile type={dentalTileTypeEnum.LINK} isInline={!isMobileView} href={'https://clients.go2dental.com/lfg/FindADentist?_cmatype=open'} action={() => {
                setBenefitsPdfError('');
                setIdCardPdfError('');
                tealiumFunction({
                    memberEvent: memberEvent,
                    eventName: tealiumFormat(findADentistTitle),
                    eventAction: TEALIUM_EVENT.EVENT_ACTION.LINK,
                });
            }} text={isTitleView ? undefined : t(DentalText.findADentistText)} hasOutArrow={!isTitleView} title={findADentistTitle} imageName='find-a-dentist' isLeftCentered={isLeftCentered}/>
        <DentalTile type={dentalTileTypeEnum.BUTTON} isInline={!isMobileView} disabled={benefitsPdfLoading} buttonRef={downloadBenefitsButtonRef} action={() => {
                if (!benefitsPdfLoading) {
                    setBenefitsPdfLoading(true);
                    setIdCardPdfError('');
                    setBenefitsPdfError('');
                    processBenefitsPdf();
                }
            }} text={isTitleView ? undefined : t(DentalText.downloadBenefitsText)} title={downloadBenefitsTitle} imageName='download' isLeftCentered={isLeftCentered}/>
      </>);
    };
    const formattedBreadcrumbName = (activeTab) => activeTab.at(0)?.toUpperCase() + activeTab.slice(1).replaceAll('_', ' ');
    return (<>
      <Breadcrumbs>
        {useHomePageBreadcrumbs()}
        {isTitleView && activeTab ? (<>
            <Breadcrumb onClick={() => {
                setActiveTab('');
                tealiumFunction({
                    memberEvent: getMemberEvent(currentMember, subscriber),
                    eventType: TEALIUM_EVENT.EVENT_TYPE.BREADCRUMB,
                    eventAction: TEALIUM_EVENT.EVENT_ACTION.LINK,
                    eventName: 'Dental',
                });
            }} href='employee/dental'>
              Dental
            </Breadcrumb>
            <Breadcrumb>{formattedBreadcrumbName(activeTab)}</Breadcrumb>
          </>) : (<Breadcrumb>{'Dental'}</Breadcrumb>)}
      </Breadcrumbs>
      {hasLanguageWarning && (<Alert type={'warning'} closeable={true} onClose={() => {
                setHasLanguageWarning(false);
            }}>
          <p>La información sobre beneficios dentales actualmente no está disponible en español.</p>
        </Alert>)}
      {(!isTitleView || !activeTab) && subscriber && (<>
          <Row>
            <Col>
              <DentalHeading isTitleView={isTitleView} text={t(DentalText.dentalTitle)}/>
            </Col>
          </Row>
          {isTitleView && (<Row>
              <MemberInformationButtons isTitleView={true}/>
            </Row>)}
          {isTitleView && benefitsPdfError && (<>
              <Row>
                <Alert type={'error'}>{t(DentalText.benefitsPdfError)}</Alert>
              </Row>
            </>)}
          {isTitleView && idCardPdfError && (<>
              <Row>
                <Alert type={'error'}>{t(DentalText.idCardPdfError)}</Alert>
              </Row>
            </>)}
        </>)}
      {subscriber ? (<>
          {isMobile ? (<>
              {isTitleView && activeTab && activeTab === COVERED_SERVICES && (<Row>
                  <Col>
                    <DentalHeading isTitleView={isMobile} text={t(DentalText.coveredServicesTitle)}/>
                  </Col>
                </Row>)}
              {isTitleView && activeTab && activeTab === DEDUCTIBLES_AND_COINSURANCE && (<Row>
                  <Col>
                    <DentalHeading isTitleView={isTitleView} text={t(DentalText.deductiblesTitle)}/>
                  </Col>
                </Row>)}
              {isTitleView && activeTab && activeTab === CLAIMS && (<Row>
                  <Col>
                    <DentalHeading isTitleView={isTitleView} text={t(DentalText.claimsTitle)}/>
                  </Col>
                </Row>)}
              {isTitleView && (benefitsPdfLoading || idCardLoading) && (<Spinner aria-busy='true' aria-live='polite' data-testid='dental-spinner'/>)}
              {(activeTab === '' || !isTitleView) && (<Row>
                  <Col>
                    <section className={'mt-0 pt-0'} aria-label={'planFundingClause'}>
                      <p data-public={true}>{subscriber.planOverview?.planFundingClause}</p>
                    </section>
                  </Col>
                </Row>)}
              <Row>
                <Col className={'d-flex small flex-column'} md={isTitleView ? 12 : 6}>
                  <MemberDropdown />
                  <MemberInformationBox />
                </Col>
                {!isTitleView && (<Col md={6}>
                    <MemberInformationButtons isInlineImageAndTitle={true} isMobileView={true}/>
                  </Col>)}
              </Row>
              {!isTitleView && benefitsPdfError && (<>
                  <Row>
                    <Col>
                      <Alert type={'error'}>{t(DentalText.benefitsPdfError)}</Alert>
                    </Col>
                  </Row>
                </>)}
              {!isTitleView && idCardPdfError && (<>
                  <Row>
                    <Col>
                      <Alert type={'error'}>{t(DentalText.idCardPdfError)}</Alert>
                    </Col>
                  </Row>
                </>)}
              {!isTitleView && (benefitsPdfLoading || idCardLoading) && (<Spinner aria-busy='true' aria-live='polite' data-testid='dental-spinner'/>)}
              <TabContent />
              {(activeTab === '' || (activeTab === COVERED_SERVICES && !isTitleView)) && (<>
                  <AdditionalResources />
                  <Row>
                    <Col style={{ color: 'gray', fontSize: '.5rem' }}>
                      <DisclaimerText action={IdCardAction} idCardLoading={idCardLoading}/>
                    </Col>
                  </Row>
                </>)}
            </>) : (<Col>
              <section className={'mt-0 pt-0 pb-0 mb-0'} aria-label={'planFundingClause'}>
                <Row style={{ padding: '.3rem .3rem .3rem .5rem' }} data-public={true}>
                  {subscriber.planOverview?.planFundingClause}
                </Row>
              </section>
              <Row className={'mt-2 pt-2'}>
                <Col xl={2} lg={3}>
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    {[subscriber, ...(subscriber.dependents || [])].map((member, index) => (<>
                        <StyledSideMenu data-testid={`navlink-${member.id}`} key={member.id} aria-current='page' data-private style={{
                        marginLeft: '0',
                        color: `${member.id === currentMember?.id
                            ? themes.publicSite.palette.primary
                            : benefitsPdfLoading ||
                                deductiblesLoading ||
                                coveredServicesLoading ||
                                claimsLoading ||
                                idCardLoading
                                ? themes.publicSite.palette.grayLighter
                                : themes.publicSite.palette.black}`,
                    }} onClick={e => {
                        if (benefitsPdfLoading ||
                            deductiblesLoading ||
                            coveredServicesLoading ||
                            claimsLoading ||
                            idCardLoading) {
                            e.preventDefault();
                        }
                        else {
                            setCurrentMember(member);
                            tealiumFunction({
                                memberEvent: getMemberEvent(member, subscriber),
                                eventAction: TEALIUM_EVENT.EVENT_ACTION.TAB,
                            });
                        }
                    }}>
                          {`${member?.firstName} ${member?.lastName}`}
                          {member.id === currentMember?.id && <StyledNavSelectIndicator />}
                        </StyledSideMenu>
                      </>))}
                  </div>
                </Col>
                <Col xl={10} lg={9}>
                  <Row className={'d-flex'}>
                    <Col md={6} className={'d-flex flex-column'}>
                      <MemberInformationBox />
                    </Col>
                    <Col md={6} className={'d-flex'}>
                      <Row>{!isTitleView && <MemberInformationButtons isLeftCentered={true}/>}</Row>
                    </Col>
                  </Row>
                  {benefitsPdfError && (<>
                      <Row>
                        <Col>
                          <Alert type={'error'}>{t(DentalText.benefitsPdfError)}</Alert>
                        </Col>
                      </Row>
                    </>)}
                  {idCardPdfError && (<>
                      <Row>
                        <Col>
                          <Alert type={'error'}>{t(DentalText.idCardPdfError)}</Alert>
                        </Col>
                      </Row>
                    </>)}
                  {(benefitsPdfLoading || idCardLoading) && (<Spinner aria-busy='true' aria-live='polite' data-testid='dental-spinner'/>)}
                  {!(benefitsPdfLoading || idCardLoading) && (<>
                      <TabContent />
                    </>)}
                  {(activeTab === COVERED_SERVICES || activeTab === '') && (<>
                      <AdditionalResources />
                      <Row>
                        <Col style={{ color: 'gray' }}>
                          <DisclaimerText action={IdCardAction} idCardLoading={idCardLoading}/>
                        </Col>
                      </Row>
                    </>)}
                </Col>
              </Row>
            </Col>)}
        </>) : (<>{(!subscriber || error) && !loading && <ErrorHtml />}</>)}
    </>);
};
export default Dental;
